import React from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Calendar,
  Search,
  Filter,
  ChevronDown,
  ArrowUpDown,
  Check,
  X,
  Edit2,
  ShoppingCart,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { MealPlanCard } from "./MealPlanCard";
import type { V32UITier } from '../../types/subscription';

// View-only types (mirrored from container)
interface MealPlan {
  id: string;
  name: string;
  weekRange: string;
  createdAt: string;
  recipeCount: number;
  servings: number;
  avgCalories: number;
  status: "completed" | "in-progress" | "archived";
  thumbnail: string;
}

interface ShoppingListItem {
  id: string;
  ingredient: {
    id: string;
    name: string;
  };
  quantity: number;
  displayUnit?: string;
  displayQty?: number;
  section?: {
    id: string;
    name: string;
  } | null;
  checked: boolean;
  notes?: string;
  sortOrder: number;
}

interface ShoppingListData {
  id: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
  items: ShoppingListItem[];
}

interface Option {
  value: string;
  label: string;
}

interface MyPlansPageViewProps {
  // Data
  plans: MealPlan[];
  shoppingListData?: ShoppingListData;
  currentTier: V32UITier;
  usageStatus?: any;

  // State
  searchTerm: string;
  statusFilter: MealPlan["status"] | "all";
  dateFilter: "all" | "this-week" | "this-month" | "custom";
  sortBy: string;
  showFilters: boolean;
  listPlanId: string | null;

  // Computed values
  isGenerating: boolean;
  hasActiveFilters: boolean;
  totalPlansCount: number;
  filteredPlansCount: number;
  hasShoppingList: boolean;

  // Options
  statusOptions: Option[];
  dateOptions: Option[];
  sortOptions: Option[];

  // Utility functions
  getStatusColor: (status: MealPlan["status"]) => string;
  getStatusLabel: (status: MealPlan["status"]) => string;

  // Event handlers - Search and filters
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (status: MealPlan["status"] | "all") => void;
  onDateFilterChange: (
    dateFilter: "all" | "this-week" | "this-month" | "custom",
  ) => void;
  onSortChange: (sort: string) => void;
  onToggleFilters: () => void;

  // Event handlers - Plan actions
  onViewPlan: (planId: string, planName: string) => void;
  onCreateNew: () => void;
  onCreateQuick: () => void;
  onDownloadPdf: (planId: string, planName: string) => void;
  onEmailPlan: (planId: string, planName: string) => void;
  onRegeneratePlan: (planId: string, planName: string) => void;
  pendingByPlan?: Record<string, { download?: boolean; email?: boolean; regenerate?: boolean }>;

  // Event handlers - Shopping list
  onViewShoppingList: (planId: string, planName: string) => void;
  onCloseShoppingList: () => void;
  onToggleShoppingItem: (itemId: string, checked: boolean) => void;
  onRefetchShoppingList: () => void;
}

/**
 * MyPlansPage View - Pure UI component with no hooks or data fetching
 *
 * This handles only:
 * - UI rendering based on props
 * - User interactions (forwarded to container via callbacks)
 * - Conditional rendering based on state
 * - No business logic, no side effects, no data fetching
 */
export default function MyPlansPageView({
  // Data
  plans,
  shoppingListData,
  currentTier,
  usageStatus,

  // State
  searchTerm,
  statusFilter,
  dateFilter,
  sortBy,
  showFilters,
  listPlanId,

  // Computed values
  isGenerating,
  hasActiveFilters,
  totalPlansCount,
  filteredPlansCount,
  hasShoppingList,

  // Options
  statusOptions,
  dateOptions,
  sortOptions,

  // Utility functions
  getStatusColor,
  getStatusLabel,

  // Event handlers - Search and filters
  onSearchChange,
  onStatusFilterChange,
  onDateFilterChange,
  onSortChange,
  onToggleFilters,

  // Event handlers - Plan actions
  onViewPlan,
  onCreateNew,
  onCreateQuick,
  onDownloadPdf,
  onEmailPlan,
  onRegeneratePlan,
  pendingByPlan,

  // Event handlers - Shopping list
  onViewShoppingList,
  onCloseShoppingList,
  onToggleShoppingItem,
  onRefetchShoppingList,
}: MyPlansPageViewProps) {
  return (
    <>
      <div className="space-y-6 px-4 py-6 sm:px-6 md:px-8">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-dusk">My Meal Plans</h1>
            <p className="text-supporting">
              View and manage all your meal plans in one place
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={onCreateNew}
              disabled={usageStatus?.hasReachedPlanLimit || isGenerating}
              className="bg-coral px-6 font-medium text-ink disabled:cursor-not-allowed disabled:opacity-50 desktop-hover:bg-coral/90"
            >
              Create New Plan
            </Button>
            <Button
              onClick={onCreateQuick}
              disabled={usageStatus?.hasReachedPlanLimit || isGenerating}
              variant="outline"
              className="border-coral text-coral hover:bg-coral hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Create Quick Plan
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="shadow-cardShadow rounded-[var(--radius-card)] border-0 bg-white">
          <CardContent className="p-6">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1">
                <Search className="text-supporting absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform" />
                <Input
                  placeholder="Search meal plans..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="border-stone bg-white pl-10 focus:border-warning focus:ring-warning/20"
                />
              </div>
              <DropdownMenu open={showFilters} onOpenChange={onToggleFilters}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="desktop-hover:bg-stone/20 border-stone bg-white text-ink"
                  >
                    <Filter className="mr-2 h-4 w-4" />
                    Filter
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Status</DropdownMenuLabel>
                  {statusOptions.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => onStatusFilterChange(option.value as any)}
                      className={
                        statusFilter === option.value ? "bg-coral/10" : ""
                      }
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}

                  <DropdownMenuSeparator />

                  <DropdownMenuLabel>Date Range</DropdownMenuLabel>
                  {dateOptions.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => onDateFilterChange(option.value as any)}
                      className={
                        dateFilter === option.value ? "bg-coral/10" : ""
                      }
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="desktop-hover:bg-stone/20 border-stone bg-white text-ink"
                  >
                    <ArrowUpDown className="mr-2 h-4 w-4" />
                    Sort
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Date</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => onSortChange("date-desc")}
                    className={sortBy === "date-desc" ? "bg-coral/10" : ""}
                  >
                    Newest First
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onSortChange("date-asc")}
                    className={sortBy === "date-asc" ? "bg-coral/10" : ""}
                  >
                    Oldest First
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuLabel>Name</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => onSortChange("name-asc")}
                    className={sortBy === "name-asc" ? "bg-coral/10" : ""}
                  >
                    A to Z
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onSortChange("name-desc")}
                    className={sortBy === "name-desc" ? "bg-coral/10" : ""}
                  >
                    Z to A
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuLabel>Recipes</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => onSortChange("recipes-desc")}
                    className={sortBy === "recipes-desc" ? "bg-coral/10" : ""}
                  >
                    Most Recipes
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onSortChange("recipes-asc")}
                    className={sortBy === "recipes-asc" ? "bg-coral/10" : ""}
                  >
                    Fewest Recipes
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuLabel>Calories</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => onSortChange("calories-desc")}
                    className={sortBy === "calories-desc" ? "bg-coral/10" : ""}
                  >
                    Highest Calories
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onSortChange("calories-asc")}
                    className={sortBy === "calories-asc" ? "bg-coral/10" : ""}
                  >
                    Lowest Calories
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card className="shadow-cardShadow rounded-[var(--radius-card)] border-0 bg-white">
            <CardContent className="p-6 text-center">
              <div className="mb-1 text-2xl font-bold text-dusk">
                {plans?.length || 0}
              </div>
              <div className="text-supporting text-sm">Total Plans</div>
            </CardContent>
          </Card>

          <Card className="shadow-cardShadow rounded-[var(--radius-card)] border-0 bg-white">
            <CardContent className="p-6 text-center">
              <div className="mb-1 text-2xl font-bold text-dusk">
                {plans?.reduce(
                  (sum, plan) => sum + (plan.recipeCount || 0),
                  0,
                ) || 0}
              </div>
              <div className="text-supporting text-sm">Total Recipes</div>
            </CardContent>
          </Card>

          <Card className="shadow-cardShadow rounded-[var(--radius-card)] border-0 bg-white">
            <CardContent className="p-6 text-center">
              <div className="mb-1 text-2xl font-bold text-dusk">
                {plans && plans.length > 0
                  ? Math.round(
                      plans.reduce(
                        (sum, plan) => sum + (plan.avgCalories || 0),
                        0,
                      ) / plans.length,
                    )
                  : 0}
              </div>
              <div className="text-supporting text-sm">Avg Daily Calories</div>
            </CardContent>
          </Card>
        </div>

        {/* Meal Plans Grid */}
        <div className="grid gap-6">
          {plans.map((plan) => {
            const pending = pendingByPlan?.[plan.id] || {};
            return (
              <MealPlanCard
                key={plan.id}
                plan={plan}
                recipes={[]} // TODO: Fetch recipes for preview when available
                pending={pending}
                onViewPlan={onViewPlan}
                onDownloadPdf={onDownloadPdf}
                onEmailPlan={onEmailPlan}
                onViewShoppingList={onViewShoppingList}
                onRegeneratePlan={onRegeneratePlan}
                disableRegenerate={!!usageStatus?.hasReachedPlanLimit}
              />
            );
          })}
        </div>

        {/* Empty State (when no plans) */}
        {plans.length === 0 && (
          <Card className="shadow-cardShadow rounded-[var(--radius-card)] border-0">
            <CardContent className="p-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-coral/10">
                <Calendar className="h-8 w-8 text-warning" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-dusk">
                No meal plans yet
              </h3>
              <p className="text-supporting mx-auto mb-6 max-w-md">
                {usageStatus?.hasReachedPlanLimit
                  ? "You've reached your weekly meal plan limit. Upgrade your subscription to create more personalized meal plans."
                  : "Ready to create your first meal plan? Let's get you started with personalized recipes."}
              </p>
              <Button
                onClick={onCreateNew}
                disabled={usageStatus?.hasReachedPlanLimit}
                className="bg-coral px-8 font-medium text-ink disabled:cursor-not-allowed disabled:opacity-50 desktop-hover:bg-coral/90"
              >
                {usageStatus?.hasReachedPlanLimit
                  ? "Upgrade to Create Plans"
                  : "Create Your First Plan"}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Shopping List Sheet */}
      <Sheet
        open={!!listPlanId}
        onOpenChange={(open) => {
          if (!open) {
            onCloseShoppingList();
          }
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Shopping List</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            {!shoppingListData || !shoppingListData.items || shoppingListData.items.length === 0 ? (
              <div className="py-8 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-coral/10">
                  <ShoppingCart className="h-8 w-8 text-coral" />
                </div>
                <h3 className="mb-2 text-lg font-medium">No shopping list items</h3>
                <p className="text-supporting text-sm">This meal plan doesn't have any shopping list items yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-cream rounded-lg p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-dusk">Shopping Progress</span>
                    <span className="text-supporting text-sm">
                      {shoppingListData.items.filter((i: ShoppingListItem) => i.checked).length} / {shoppingListData.items.length} completed
                    </span>
                  </div>
                  <div className="bg-stone/20 h-2 w-full rounded-full">
                    <div
                      className="h-2 rounded-full bg-coral transition-all duration-300"
                      style={{ width: `${(shoppingListData.items.filter((i: ShoppingListItem) => i.checked).length / shoppingListData.items.length) * 100}%` }}
                    />
                  </div>
                </div>

                {Object.entries(
                  shoppingListData.items.reduce((groups: Record<string, ShoppingListItem[]>, item: ShoppingListItem) => {
                    const key = item.section?.name || "Other";
                    if (!groups[key]) {
                      groups[key] = [];
                    }
                    groups[key].push(item);
                    return groups;
                  }, {} as Record<string, ShoppingListItem[]>)
                ).map(([sectionName, items]) => (
                  <div key={sectionName} className="border-stone/20 rounded-lg border p-4">
                    <h3 className="mb-3 flex items-center gap-2 font-medium text-martinique">
                      <div className="bg-sage h-3 w-3 rounded-full" />
                      {sectionName}
                      <Badge variant="secondary" className="ml-auto">{(items as ShoppingListItem[]).length} items</Badge>
                    </h3>
                    <div className="space-y-2">
                      {(items as ShoppingListItem[]).map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between rounded-lg border px-3 py-3 transition-colors ${item.checked ? "border-sage/20 bg-sage/10" : "border-stone/10 bg-white"}`}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => onToggleShoppingItem(item.id, !item.checked)}
                              className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${item.checked ? "border-coral bg-coral text-white" : "border-stone bg-white hover:border-coral"}`}
                            >
                              {item.checked && <Check className="h-3 w-3" />}
                            </button>
                            <div className={item.checked ? "line-through opacity-60" : ""}>
                              <div className="text-sm font-medium text-dusk">{item.ingredient.name}</div>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-supporting">
                                  <span className="font-medium">{item.displayQty} {item.displayUnit}</span>
                                  {item.notes && <span className="ml-1">• {item.notes}</span>}
                                </span>
                                {!item.checked && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); }}
                                    className="text-stone/60 transition-colors hover:text-coral"
                                    title="Adjust quantity"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          {item.checked && (
                            <button
                              onClick={() => onToggleShoppingItem(item.id, false)}
                              className="text-stone transition-colors hover:text-coral"
                              title="Uncheck item"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
